import type { WebContainer } from '@webcontainer/api';
import { hashFileContent } from '../utils/workbenchFileHash';

const OPFS_ROOT = 'wc-deps';

function supportsOpfs(): boolean {
  return typeof navigator !== 'undefined' && 'storage' in navigator && 'getDirectory' in navigator.storage;
}

export function getTemplateLockHash(packageLockJson: string): string {
  return hashFileContent(packageLockJson);
}

async function opfsFilePath(lockHash: string): Promise<FileSystemFileHandle | null> {
  if (!supportsOpfs()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    const depsDir = await root.getDirectoryHandle(OPFS_ROOT, { create: true });
    return depsDir.getFileHandle(`${lockHash}.bin`, { create: true });
  } catch {
    return null;
  }
}

const MIN_DEPS_SNAPSHOT_BYTES = 1024;

/** Reject HTML/JSON error pages and empty blobs mistaken for wc binary snapshots. */
export function looksLikeWebContainerBinarySnapshot(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < MIN_DEPS_SNAPSHOT_BYTES) return false;
  const head = new Uint8Array(buffer, 0, Math.min(16, buffer.byteLength));
  if (head[0] === 0x3c) return false; // '<' HTML
  if (head[0] === 0x7b) return false; // '{' JSON
  return true;
}

export async function removeDepsCacheFromOpfs(lockHash: string): Promise<void> {
  if (!supportsOpfs()) return;
  try {
    const root = await navigator.storage.getDirectory();
    const depsDir = await root.getDirectoryHandle(OPFS_ROOT);
    await depsDir.removeEntry(`${lockHash}.bin`);
  } catch {
    /* already gone */
  }
}

export async function readDepsCacheFromOpfs(lockHash: string): Promise<ArrayBuffer | null> {
  if (!supportsOpfs()) return null;
  try {
    const root = await navigator.storage.getDirectory();
    const depsDir = await root.getDirectoryHandle(OPFS_ROOT);
    const handle = await depsDir.getFileHandle(`${lockHash}.bin`);
    const file = await handle.getFile();
    if (file.size === 0) return null;
    const buf = await file.arrayBuffer();
    if (!looksLikeWebContainerBinarySnapshot(buf)) {
      await removeDepsCacheFromOpfs(lockHash);
      return null;
    }
    return buf;
  } catch {
    return null;
  }
}

export async function writeDepsCacheToOpfs(lockHash: string, data: ArrayBuffer): Promise<void> {
  const handle = await opfsFilePath(lockHash);
  if (!handle) return;
  const writable = await handle.createWritable();
  // A `FileSystemWritableFileStream` holds an exclusive lock on the entry until it is closed or
  // aborted. Without this, a throwing `write()` (quota, eviction, contention) skipped `close()`
  // and leaked that lock for the rest of the page's life — every later write to the same
  // `<lockHash>.bin` then failed too, and the caller's outer catch swallowed it silently.
  try {
    await writable.write(data);
    await writable.close();
  } catch (err) {
    try {
      await writable.abort();
    } catch {
      /* stream already errored out — the lock is released either way */
    }
    throw err;
  }
}

/** OPFS/binary mounts drop execute bits on `node_modules/.bin/*`, breaking `npm run dev`. */
export async function fixNodeModulesBinPermissions(wc: WebContainer): Promise<void> {
  try {
    const proc = await wc.spawn('chmod', ['-R', '755', 'node_modules/.bin']);
    await proc.exit;
  } catch {
    /* best-effort — callers can still use npx */
  }
}

export async function mountDepsFromBuffer(
  wc: WebContainer,
  buffer: ArrayBuffer,
): Promise<boolean> {
  if (!looksLikeWebContainerBinarySnapshot(buffer)) {
    return false;
  }
  try {
    // WebContainer requires the mount point directory to exist before mount().
    await wc.fs.mkdir('node_modules', { recursive: true });
    await wc.mount(buffer, { mountPoint: 'node_modules' });
    await fixNodeModulesBinPermissions(wc);
    return true;
  } catch {
    return false;
  }
}

function uint8ToArrayBuffer(view: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(view.byteLength);
  new Uint8Array(copy).set(view);
  return copy;
}

export async function exportAndCacheNodeModules(
  wc: WebContainer,
  lockHash: string,
): Promise<void> {
  try {
    const exported = await wc.export('node_modules', { format: 'binary' });
    const buf =
      exported instanceof ArrayBuffer
        ? exported
        : exported instanceof Uint8Array
          ? uint8ToArrayBuffer(exported)
          : null;
    if (buf && buf.byteLength > 0) {
      await writeDepsCacheToOpfs(lockHash, buf);
    }
  } catch {
    /* best-effort cache */
  }
}

const PREBUILT_CACHE = 'wc-prebuilt-deps-v1';

export async function fetchPrebuiltNodeModules(
  lockHash: string,
  publicUrl: string,
): Promise<ArrayBuffer | null> {
  if (typeof caches !== 'undefined') {
    try {
      const cache = await caches.open(PREBUILT_CACHE);
      const cached = await cache.match(publicUrl);
      if (cached) {
        const buf = await cached.arrayBuffer();
        if (looksLikeWebContainerBinarySnapshot(buf)) {
          return buf;
        }
        await cache.delete(publicUrl);
      }
    } catch {
      /* ignore */
    }
  }

  try {
    const res = await fetch(publicUrl, { priority: 'low' } as RequestInit);
    if (!res.ok) return null;
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('text/html')) return null;
    const buf = await res.arrayBuffer();
    if (!looksLikeWebContainerBinarySnapshot(buf)) return null;
    if (typeof caches !== 'undefined' && buf.byteLength > 0) {
      try {
        const cache = await caches.open(PREBUILT_CACHE);
        await cache.put(publicUrl, new Response(buf.slice(0)));
      } catch {
        /* ignore */
      }
    }
    return buf;
  } catch {
    return null;
  }
}

export function getPrebuiltNodeModulesUrl(lockHash: string): string {
  const base = typeof import.meta !== 'undefined' && import.meta.env?.BASE_URL
    ? String(import.meta.env.BASE_URL)
    : '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  return `${normalizedBase}wc/node_modules-${lockHash}.bin`;
}
